// 세션 만료 감지 기능이 포함된 CSRF 토큰 관리

import axios from 'axios';
import { getDjangoEndpoint } from 'src/utils/apiEndpoints';


// CSRF 토큰 및 fetch 중복 방지를 위한 전역 변수
let csrfToken: string | null = null;
let fetchPromise: Promise<string | null> | null = null;

// 세션 만료 콜백 함수 (외부에서 설정 가능)
let onSessionExpired: (() => void) | null = null;

/**
 * 세션 만료 시 실행할 콜백 설정
 */
export const setSessionExpiredCallback = (callback: () => void) => {
  onSessionExpired = callback;
};

/**
 * 세션 만료 처리
 */
const handleSessionExpired = () => {
  
  // 토큰 무효화
  csrfToken = null;
  fetchPromise = null;
  
  // 외부에서 설정한 콜백 실행 (로그인 페이지 리다이렉트 등)
  if (onSessionExpired) {
    onSessionExpired();
  } else {
    // 기본 동작: 로그인 페이지로 리다이렉트
    window.location.href = '/login';
  }
};

// CSRF 토큰 요청 함수 (세션 감지 기능 포함)
const fetchCsrfToken = async (): Promise<string | null> => {
  // 이미 토큰이 있다면 바로 반환 (중복 요청 방지)
  if (csrfToken) {
    return csrfToken;
  }

  // 이미 요청 중이라면 기존 fetchPromise 반환
  if (fetchPromise) {
    return fetchPromise;
  }

  const djangoEndpoint = getDjangoEndpoint();

  if (!djangoEndpoint) {
    console.error("aws-exports.json에서 Django endpoint를 찾을 수 없습니다.");
    return null;
  }

  // 새 fetchPromise 생성 (요청 중 상태 공유)
  fetchPromise = new Promise(async (resolve) => {
    try {
      const response = await axios.get(`${djangoEndpoint}/get-csrf-token/`, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        timeout: 10000 // 10초 타임아웃 설정
      });

      // 1. 응답 본문에서 토큰 추출
      if (response.data && response.data.csrfToken) {
        csrfToken = response.data.csrfToken;
        resolve(csrfToken);
        return;
      }

      if (response.data && response.data.csrf_token) {
        csrfToken = response.data.csrf_token;
        resolve(csrfToken);
        return;
      }

      // 2. 응답 헤더에서 토큰 추출 시도
      const possibleHeaderNames = [
        'x-csrftoken',
        'X-CSRFToken', 
        'csrftoken',
        'CSRFToken'
      ];

      for (const headerName of possibleHeaderNames) {
        try {
          const headerToken = response.headers[headerName.toLowerCase()] || response.headers[headerName];
          if (headerToken) {
            csrfToken = headerToken;
            resolve(csrfToken);
            return;
          }
        } catch (e) {
          console.error(`헤더에서 CSRF 토큰 추출 실패: ${headerName}`, e);
        }
      }

      // 3. 토큰을 찾지 못한 경우
      console.error('CSRF 토큰을 찾을 수 없습니다. 응답 데이터:', response.data);
      console.error('응답 헤더:', response.headers);
      
      // 토큰이 없다는 것은 세션 문제일 가능성이 높음
      handleSessionExpired();
      resolve(null);
      
    } catch (error) {
      console.error('CSRF 토큰 가져오기 실패:', error);
      
      // 세션 만료 감지
      if (error.response?.status === 401) {
        handleSessionExpired();
        resolve(null);
        return;
      }
      
      if (error.response?.status === 403) {
        handleSessionExpired();
        resolve(null);
        return;
      }

      // 네트워크 에러 또는 타임아웃
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        handleSessionExpired();
        resolve(null);
        return;
      }

      // 기타 네트워크 에러
      if (!error.response) {
        handleSessionExpired();
        resolve(null);
        return;
      }

      // 기타 서버 에러
      console.error('에러 상세 정보:', error.response?.status, error.response?.data);
      
      // 5xx 에러도 세션 관련 문제일 수 있음
      if (error.response?.status >= 500) {
        handleSessionExpired();
      }
      
      resolve(null);
      
    } finally {
      fetchPromise = null;
    }
  });

  return fetchPromise;
};

// 토큰 무효화 및 세션 만료 처리
export const invalidateCsrfToken = (triggerSessionExpired: boolean = false) => {
  csrfToken = null;
  fetchPromise = null;
  
  if (triggerSessionExpired) {
    handleSessionExpired();
  }
};

// 토큰 강제 새로고침
export const refreshCsrfToken = async (): Promise<string | null> => {
  csrfToken = null;
  fetchPromise = null;
  return await fetchCsrfToken();
};

// 세션 상태 확인 (토큰 요청으로 세션 검증)
export const checkSessionStatus = async (): Promise<boolean> => {
  
  try {
    const token = await fetchCsrfToken();
    
    if (token) {
      return true;
    } else {
      return false;
    }
  } catch (error) {
    return false;
  }
};

// 현재 토큰 상태 확인 (디버깅용)
export const getCsrfTokenInfo = () => {
  const info = {
    hasToken: !!csrfToken,
    token: csrfToken,
    isRequesting: !!fetchPromise
  };
  return info;
};

export default fetchCsrfToken;